import React, { useState, useRef, useEffect, useId } from 'react';
import PropTypes from 'prop-types';
import {
  DropdownContainer,
  DropdownButton,
  DropdownButtonText,
  DropdownArrows,
  DropdownMenu,
  DropdownOption,
} from './SimplrDropdown.styles';

const SimplrDropdown = ({ selected, options, onSelect, placeholder = 'Select option', disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  // NOTE(vlad|2026-02-12): Generate unique per-instance ID to prevent ARIA ID collisions when multiple dropdowns exist on the page
  const instanceId = useId();
  const listboxId = `dropdown-listbox-${instanceId}`;
  const getOptionId = (index) => `option-${instanceId}-${index}`;

  const getSelectedLabel = () => {
    if (!selected) {
      return placeholder;
    }

    const selectedOption = options.find((opt) => opt.id === selected);

    return selectedOption ? selectedOption.label : placeholder;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // NOTE(vlad|2026-02-12): Initialize focused index when opening dropdown
  // Sets focus to currently selected option, or first option if none selected
  // This ensures keyboard navigation starts from a logical position
  useEffect(() => {
    if (isOpen && options.length > 0) {
      const selectedIndex = options.findIndex((opt) => opt.id === selected);
      setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  }, [isOpen, selected, options]);

  // NOTE(vlad|2026-02-12): Full keyboard navigation support for accessibility
  // Implements ARIA combobox pattern: Enter/Space to select, Arrows to navigate, Escape to close, Home/End to jump
  const handleKeyDown = (event) => {
    if (disabled) {
      return;
    }

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else if (focusedIndex >= 0 && focusedIndex < options.length) {
          handleSelect(options[focusedIndex].id);
        }
        break;

      case 'Escape':
        event.preventDefault();
        if (isOpen) {
          setIsOpen(false);
          buttonRef.current?.focus();
        }
        break;

      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setFocusedIndex((prev) => (prev < options.length - 1 ? prev + 1 : prev));
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        }
        break;

      case 'Home':
        event.preventDefault();
        if (isOpen) {
          setFocusedIndex(0);
        }
        break;

      case 'End':
        event.preventDefault();
        if (isOpen) {
          setFocusedIndex(options.length - 1);
        }
        break;

      default:
        break;
    }
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (optionId) => {
    onSelect(optionId);
    setIsOpen(false);
    // NOTE(vlad|2026-02-12): Return focus to button after selection for proper keyboard navigation flow
    buttonRef.current?.focus();
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton
        ref={buttonRef}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        isOpen={isOpen}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-label={placeholder}
        aria-activedescendant={isOpen && focusedIndex >= 0 ? getOptionId(focusedIndex) : undefined}
        tabIndex={disabled ? -1 : 0}
      >
        <DropdownButtonText>{getSelectedLabel()}</DropdownButtonText>
        <DropdownArrows>
          <img
            src={isOpen ? '/images/new-designs/chevron-up-blue.svg' : '/images/new-designs/chevron-down-blue.svg'}
            alt=""
          />
        </DropdownArrows>
      </DropdownButton>

      {isOpen && (
        <DropdownMenu ref={menuRef} role="listbox" id={listboxId} aria-label={placeholder}>
          {options.map((option, index) => (
            <DropdownOption
              key={option.id}
              id={getOptionId(index)}
              role="option"
              aria-selected={option.id === selected}
              onClick={() => handleSelect(option.id)}
              onMouseEnter={() => setFocusedIndex(index)}
              isSelected={option.id === selected}
              isFocused={index === focusedIndex}
            >
              {option.label}
            </DropdownOption>
          ))}
        </DropdownMenu>
      )}
    </DropdownContainer>
  );
};

SimplrDropdown.propTypes = {
  selected: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
};

export default SimplrDropdown;
